import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Autocomplete,
  Box,
  Button,
  IconButton,
  ListSubheader,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import { Add, Close } from '@mui/icons-material';
import { useQuery } from '@tanstack/react-query';
import { Link as RouterLink } from 'react-router-dom';
import {
  integrationApiService,
  FieldMapping,
  MetafieldMappingTemplate,
  MetafieldSource,
  StoreMetafieldDefinitionOption,
} from '../services/integrationApi';
import { tokens } from '../theme/tokens';
import { EmptyState, InlineMessage, SectionCard, StatusChip } from './ui';
import { useAppTranslation } from '../i18n/useAppTranslation';
import { metafieldTypeLabel, motzuKeySet, visibleStoreDefinitions } from './mapping/mappingModel';
import {
  applyTemplateRows,
  definitionsById,
  mappingRowState,
  SPEC_SOURCE_PREFIX,
  specSourcesFromCatalog,
  writingRowCount,
} from './mapping/metafieldMappingModel';

export type MetafieldMappingRow = FieldMapping;

interface ShopifyMetafieldMappingSectionProps {
  integrationId: string;
  rows: MetafieldMappingRow[];
  onChange: (rows: MetafieldMappingRow[]) => void;
  /**
   * The plan lacks `customMetafieldMapping` (MOT-574): only the price-bearing sources (MAP, prices, the
   * rebate and its window) are locked; identity, spec, launch and compatibility rows map on every plan.
   */
  planLocked: boolean;
}

/** Source | target | state | remove; on a phone the target and state wrap under the source. */
const TABLE_COLUMNS = { xs: 'minmax(0,1fr) 32px', md: 'minmax(0,1.1fr) minmax(0,1.2fr) minmax(0,0.9fr) 32px' };
const FULL_ROW_ON_PHONE = (order: number) => ({ gridColumn: { xs: '1 / -1', md: 'auto' }, order: { xs: order, md: 0 }, minWidth: 0 });
const GROUP_ORDER: MetafieldSource['group'][] = ['pricing', 'rebate', 'launch', 'compatibility', 'identity', 'spec'];
/** Mirrors `isPriceBearingSource` on the API: these groups need the plan key. */
const PRICE_GROUPS: ReadonlySet<MetafieldSource['group']> = new Set<MetafieldSource['group']>(['pricing', 'rebate']);

/**
 * Any Motzu field onto a metafield the store already defined (MOT-144, flag
 * `shopify_metafield_mapping`). Below the spec table: specs stay there; this
 * table adds MAP, the live rebate and its window, launch date, pre-order and
 * compatibility. Nothing is created in Shopify; a row whose definition does
 * not exist yet says what to create.
 */
const ShopifyMetafieldMappingSection: React.FC<ShopifyMetafieldMappingSectionProps> = ({
  integrationId,
  rows,
  onChange,
  planLocked,
}) => {
  const { t } = useAppTranslation();
  const [templateId, setTemplateId] = useState('');
  // A stable client-side id per row, so a row keeps its DOM (and the target input its focus) while its
  // editable values change. Reconciled by position when the parent adds, removes or replaces rows.
  const nextRowId = useRef(0);
  const newRowId = () => `row-${nextRowId.current++}`;
  const [rowIds, setRowIds] = useState<string[]>(() => rows.map(() => newRowId()));
  useEffect(() => {
    if (rowIds.length === rows.length) return;
    setRowIds((ids) => (ids.length > rows.length
      ? ids.slice(0, rows.length)
      : [...ids, ...rows.slice(ids.length).map(() => newRowId())]));
  }, [rows.length, rowIds.length]);
  const optionsQuery = useQuery({
    queryKey: ['shopify-spec-metafield-options', integrationId],
    queryFn: () => integrationApiService.getSpecMetafieldOptions(integrationId),
    staleTime: 5 * 60 * 1000,
  });
  const fieldsQuery = useQuery({
    queryKey: ['motzu-metafield-fields'],
    queryFn: () => integrationApiService.getMotzuMetafieldFields(),
    staleTime: Infinity,
  });
  const mapping = optionsQuery.data?.metafieldMapping ?? null;
  // Motzu's own fields are never a target: the API refuses them, and the spec table hides them the same way.
  const motzuKeys = useMemo(() => motzuKeySet(fieldsQuery.data || []), [fieldsQuery.data]);
  const definitions = useMemo(
    () => visibleStoreDefinitions(optionsQuery.data?.definitions || [], motzuKeys),
    [optionsQuery.data, motzuKeys],
  );
  const byId = useMemo(() => definitionsById(definitions), [definitions]);
  const sources = useMemo<MetafieldSource[]>(() => {
    const registry = mapping?.sources || [];
    return [...registry, ...specSourcesFromCatalog(optionsQuery.data?.specCatalog || [], rows)];
  }, [mapping, optionsQuery.data, rows]);
  const sourceById = useMemo(() => new Map(sources.map((source) => [source.id, source])), [sources]);
  const templates = useMemo<MetafieldMappingTemplate[]>(() => mapping?.templates || [], [mapping]);

  if (!mapping) return null;

  const writing = writingRowCount(rows, sourceById, byId);
  const isLockedSource = (sourceField: string) => {
    const source = sourceById.get(sourceField);
    return planLocked && !!source && PRICE_GROUPS.has(source.group);
  };

  const update = (index: number, patch: Partial<MetafieldMappingRow>) => {
    onChange(rows.map((row, i) => (i === index ? { ...row, ...patch } : row)));
  };
  const remove = (index: number) => {
    setRowIds((ids) => ids.filter((_, i) => i !== index));
    onChange(rows.filter((_, i) => i !== index));
  };
  const addRow = () => {
    setRowIds((ids) => [...ids, newRowId()]);
    onChange([...rows, { sourceField: '', targetField: '' }]);
  };
  const fillTemplate = () => {
    const template = templates.find((entry) => entry.id === templateId);
    if (!template) return;
    // Without the plan key a template brings only the rows the store can map.
    onChange(applyTemplateRows(rows, planLocked
      ? { ...template, rows: template.rows.filter((row) => !isLockedSource(row.sourceField)) }
      : template));
  };

  const groupLabel = (group: MetafieldSource['group']) => t(`integrations.metafieldMapping.groups.${group}`);
  const sourceOptions = GROUP_ORDER.flatMap((group) => {
    const inGroup = sources.filter((source) => source.group === group);
    if (inGroup.length === 0) return [];
    return [
      <ListSubheader key={`group-${group}`}>{groupLabel(group)}</ListSubheader>,
      ...inGroup.map((source) => (
        <MenuItem key={source.id} value={source.id} disabled={isLockedSource(source.id)}>
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ fontSize: 13, fontWeight: 600 }} noWrap>{source.label}</Typography>
            <Typography sx={{ fontSize: 11.5, color: tokens.ink.muted }} noWrap>{source.description}</Typography>
          </Box>
        </MenuItem>
      )),
    ];
  });

  return (
    <SectionCard
      dense
      title={t('integrations.metafieldMapping.title')}
      subtitle={t('integrations.metafieldMapping.subtitle')}
      actions={(
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
          <Select
            size="small"
            value={templateId}
            displayEmpty
            disabled={templates.length === 0}
            onChange={(event) => setTemplateId(String(event.target.value))}
            sx={{ minWidth: 180, fontSize: 13 }}
            inputProps={{ 'aria-label': t('integrations.metafieldMapping.templatePicker') }}
          >
            <MenuItem value="">{t('integrations.metafieldMapping.templatePicker')}</MenuItem>
            {templates.map((template) => (
              <MenuItem key={template.id} value={template.id}>{template.name}</MenuItem>
            ))}
          </Select>
          <Button size="small" variant="outlined" disabled={!templateId} onClick={fillTemplate}>
            {t('integrations.metafieldMapping.fillFromTemplate')}
          </Button>
        </Box>
      )}
    >
      <Box sx={{ px: 2, py: 1.25, borderBottom: '1px solid', borderColor: tokens.line.hairline }}>
        <Typography sx={{ fontSize: 13, color: tokens.ink.body }}>
          <Box component="span" sx={{ fontWeight: 700 }}>
            {t('integrations.metafieldMapping.coverage', { writing, total: rows.length })}
          </Box>
          {' '}{t('integrations.metafieldMapping.coverageNote')}
        </Typography>
      </Box>

      {planLocked && (
        <Box sx={{ px: 2, pt: 2 }}>
          <InlineMessage severity="info">
            {t('integrations.metafieldMapping.planLocked')}{' '}
            <Button component={RouterLink} to="/app/billing" size="small" sx={{ p: 0, minWidth: 0, fontWeight: 700, verticalAlign: 'baseline' }}>
              {t('integrations.metafieldMapping.planLockedAction')}
            </Button>
          </InlineMessage>
        </Box>
      )}

      {rows.length === 0 ? (
        <EmptyState
          size="compact"
          title={t('integrations.metafieldMapping.emptyTitle')}
          description={t('integrations.metafieldMapping.emptyBody')}
          actionLabel={t('integrations.metafieldMapping.addRow')}
          onAction={addRow}
        />
      ) : (
        <>
          <Box sx={{ display: { xs: 'none', md: 'grid' }, gridTemplateColumns: TABLE_COLUMNS, px: 2, height: 34, alignItems: 'center', bgcolor: tokens.surface.sunken }}>
            {['source', 'target', 'state'].map((column) => (
              <Typography key={column} sx={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: tokens.ink.muted }}>
                {t(`integrations.metafieldMapping.columns.${column}`)}
              </Typography>
            ))}
            <span />
          </Box>
          {rows.map((row, index) => {
            const source = sourceById.get(row.sourceField);
            const state = mappingRowState(row, source, byId);
            const definition: StoreMetafieldDefinitionOption | undefined = byId.get(row.targetField);
            // A price row kept from a higher plan stays visible and removable, but cannot be edited.
            const rowLocked = isLockedSource(row.sourceField);
            return (
              <Box
                key={rowIds[index] ?? `row-${index}`}
                sx={{
                  display: 'grid',
                  gridTemplateColumns: TABLE_COLUMNS,
                  gap: 1,
                  alignItems: 'center',
                  px: 2,
                  py: 1.25,
                  borderTop: '1px solid',
                  borderColor: tokens.line.hairline,
                }}
              >
                <Select
                  size="small"
                  value={sourceById.has(row.sourceField) ? row.sourceField : ''}
                  displayEmpty
                  disabled={rowLocked}
                  onChange={(event) => update(index, { sourceField: String(event.target.value) })}
                  renderValue={(value) => {
                    const picked = sourceById.get(String(value));
                    return picked ? picked.label : (
                      <Typography component="span" sx={{ color: tokens.ink.faint, fontSize: 13 }}>
                        {t('integrations.metafieldMapping.pickSource')}
                      </Typography>
                    );
                  }}
                  sx={{ fontSize: 13 }}
                  inputProps={{ 'aria-label': t('integrations.metafieldMapping.columns.source') }}
                >
                  {sourceOptions}
                </Select>
                <Box sx={FULL_ROW_ON_PHONE(1)}>
                  <Autocomplete
                    size="small"
                    freeSolo
                    disabled={rowLocked}
                    options={definitions.map((entry) => `${entry.namespace}.${entry.key}`)}
                    value={row.targetField}
                    onChange={(_event, value) => update(index, { targetField: String(value || '').trim() })}
                    onInputChange={(_event, value, reason) => {
                      if (reason === 'input') update(index, { targetField: value.trim() });
                    }}
                    renderOption={(props, option) => {
                      const entry = byId.get(option);
                      return (
                        <li {...props} key={option}>
                          <Box sx={{ minWidth: 0 }}>
                            <Typography sx={{ fontSize: 13, fontWeight: 600 }} noWrap>{entry?.name || option}</Typography>
                            <Typography sx={{ fontFamily: tokens.font.mono, fontSize: 11.5, color: tokens.ink.muted }} noWrap>
                              {option}{entry ? ` · ${metafieldTypeLabel(entry.type)}` : ''}
                            </Typography>
                          </Box>
                        </li>
                      );
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        placeholder={t('integrations.metafieldMapping.pickTarget')}
                        inputProps={{ ...params.inputProps, 'aria-label': t('integrations.metafieldMapping.columns.target') }}
                      />
                    )}
                  />
                </Box>
                <Box sx={{ ...FULL_ROW_ON_PHONE(2), display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                  <StatusChip
                    label={t(`integrations.metafieldMapping.state.${state.code}`, {
                      type: definition ? metafieldTypeLabel(definition.type) : '',
                      expected: state.expectedType ? metafieldTypeLabel(state.expectedType) : '',
                    })}
                    tone={state.tone}
                  />
                  {state.code === 'noDefinition' && row.targetField && state.expectedType && (
                    <Typography sx={{ fontSize: 11.5, color: tokens.ink.muted }}>
                      {t('integrations.metafieldMapping.createHint', { target: row.targetField, type: metafieldTypeLabel(state.expectedType) })}
                    </Typography>
                  )}
                </Box>
                <IconButton
                  size="small"
                  aria-label={t('integrations.metafieldMapping.removeRow')}
                  onClick={() => remove(index)}
                >
                  <Close sx={{ fontSize: 16, color: tokens.ink.muted }} />
                </IconButton>
              </Box>
            );
          })}
          <Box sx={{ px: 2, py: 1.25, borderTop: '1px solid', borderColor: tokens.line.hairline, display: 'flex', justifyContent: 'space-between', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
            <Button size="small" startIcon={<Add />} onClick={addRow}>
              {t('integrations.metafieldMapping.addRow')}
            </Button>
            <Typography sx={{ fontSize: 12, color: tokens.ink.muted }}>
              {t('integrations.metafieldMapping.footer', { prefix: SPEC_SOURCE_PREFIX })}
            </Typography>
          </Box>
        </>
      )}
    </SectionCard>
  );
};

export default ShopifyMetafieldMappingSection;
